import type {
  CohortFilters,
  QuestionsPayload,
  ResultsMap,
  User,
  UserVotes,
} from "./types";

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { error: "invalid_json" };
  }
  if (!res.ok) {
    const err = body as { error?: string };
    throw new ApiError(err.error || `http_${res.status}`, res.status, body);
  }
  return body as T;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

// ---- Questions (static, served from /public) ----
let questionsCache: Promise<QuestionsPayload> | null = null;
export function loadQuestions(): Promise<QuestionsPayload> {
  if (!questionsCache) {
    questionsCache = fetch("/questions.json").then((r) => r.json());
  }
  return questionsCache;
}

// ---- Auth ----
export async function register(email: string, username: string) {
  return jsonFetch<{ ok: true; intent: "register" | "login" }>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, username }),
  });
}

export async function verifyMagicToken(token: string) {
  return jsonFetch<{ ok: true; userId: string }>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function logout() {
  return jsonFetch<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

// ---- Me ----
export async function getMe(): Promise<User | null> {
  try {
    const res = await jsonFetch<{ user: User }>("/api/me");
    return res.user;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export async function updateMe(patch: Partial<Record<keyof User, string | null>>) {
  return jsonFetch<{ ok: true; updated: number }>("/api/me", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

// ---- Votes ----
export async function getMyVotes(): Promise<UserVotes> {
  try {
    const res = await jsonFetch<{ votes: UserVotes }>("/api/votes/cast");
    return res.votes;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return {};
    throw e;
  }
}

export async function castVote(questionId: string, optionKey: string) {
  return jsonFetch<{ ok: true; questionId: string; optionKey: string }>("/api/votes/cast", {
    method: "POST",
    body: JSON.stringify({ questionId, optionKey }),
  });
}

export async function getResults(opts?: {
  questionId?: string;
  filters?: CohortFilters;
}): Promise<{ results: ResultsMap; cohortFilterApplied: boolean; minBucket: number | null }> {
  const params = new URLSearchParams();
  if (opts?.questionId) params.set("questionId", opts.questionId);
  if (opts?.filters) {
    for (const [k, v] of Object.entries(opts.filters)) {
      if (v) params.set(k, v);
    }
  }
  const qs = params.toString();
  return jsonFetch<{
    results: ResultsMap;
    cohortFilterApplied: boolean;
    minBucket: number | null;
  }>(`/api/votes/results${qs ? `?${qs}` : ""}`);
}
